// ─── Onboarding Chat — Path-2 chat-driven onboarding (ocn-S545-P0) ────
// app idea -> checkout -> (optional survey) -> building -> live app,
// driven entirely by the EXISTING /api/onboarding-chat/* backend routes
// (ocn-S419-P0/S520-P0/S521-P0/S526-P0, owned by the ocn-cc lane — never
// modified here). Activates only under the `?onboarding=1` fresh-signup /
// unsubscribed gate wired in App.tsx. Reuses the SubscriberChat bubble/
// composer visual language rather than introducing a new design system.
//
// GROUND-TRUTHED STEP ORDER (mc-S662 scar avoidance — do not trust prose
// over code): the backend's OnboardingChatState machine
// (apps/api/src/lib/onboarding-chat.ts advanceOnboardingStep) enforces
// qa -> payment -> trial_explain -> app_idea -> credential_survey ->
// handoff for Factory (Forge skips app_idea). The app-idea route 409s
// unless state.step === 'app_idea', which only becomes true AFTER
// payment_confirmed + trial_explained fire — i.e. CHECKOUT happens
// BEFORE app-idea capture is reachable, even though this spec's own
// prose sections describe three mutually-inconsistent orderings. This
// file follows the real, enforced backend order so the wiring actually
// functions; see specs/handoffs/ocn-s545-p0-report.md for detail.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  onboardingChatApi,
  isMaybeFireEnabled,
  initialOnboardingChatState,
  type OnboardingChatState,
  type OfferingId,
  type StripeDiscount,
} from '../hooks/useOnboardingChat';

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');
  }
  return stripePromise;
}

interface Bubble {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

let bubbleCounter = 0;
function nextBubbleId(): string {
  bubbleCounter += 1;
  return `ob-${Date.now()}-${bubbleCounter}`;
}

type BuildStatus = 'idle' | 'loading' | 'placeholder' | 'enqueued' | 'missing_app_idea' | 'error';

interface BuildInfo {
  status: BuildStatus;
  buildId?: string;
  alreadyEnqueued?: boolean;
  reason?: string;
}

export type Screen = 'qa' | 'payment' | 'app_idea' | 'building' | 'survey';

export function computeScreen(
  chatState: OnboardingChatState,
  specSessionId: string | null,
  postBuildPhase: 'idle' | 'survey',
): Screen {
  if (postBuildPhase === 'survey') return 'survey';
  if (chatState.offeringChoice === 'factory' && specSessionId) return 'building';
  if (chatState.step === 'payment' || chatState.step === 'trial_explain') return 'payment';
  if (chatState.step === 'app_idea') return 'app_idea';
  if (chatState.step === 'credential_survey' || chatState.step === 'handoff') return 'survey';
  return 'qa';
}

export interface OnboardingChatProps {
  /** Called once the subscriber is ready to leave the onboarding surface
   *  (payment already succeeded server-side; App.tsx's gate re-evaluates
   *  after this via a subscriber refetch). */
  onFinish: () => void;
}

export default function OnboardingChat({ onFinish }: OnboardingChatProps): React.ReactElement {
  const [chatState, setChatState] = useState<OnboardingChatState>(initialOnboardingChatState());
  const [bubbles, setBubbles] = useState<Bubble[]>([
    { id: nextBubbleId(), role: 'assistant', text: "Welcome — I'll get you set up. Ask me anything about pricing, credits, or how ownership works, or just pick Forge or Factory below when you're ready." },
  ]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [specSessionId, setSpecSessionId] = useState<string | null>(null);
  const [postBuildPhase, setPostBuildPhase] = useState<'idle' | 'survey'>('idle');
  const [buildInfo, setBuildInfo] = useState<BuildInfo>({ status: 'idle' });
  const buildTriggered = useRef(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  const appendBubble = useCallback((role: Bubble['role'], text: string) => {
    setBubbles(prev => [...prev, { id: nextBubbleId(), role, text }]);
  }, []);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [bubbles.length]);

  const screen = computeScreen(chatState, specSessionId, postBuildPhase);

  // ─── QA composer (step: qa) ─────────────────────────────────
  const sendQa = useCallback(async () => {
    const msg = draft.trim();
    if (!msg) return;
    setDraft('');
    appendBubble('user', msg);
    setBusy(true);
    try {
      const res = await onboardingChatApi.qa(msg);
      appendBubble('assistant', res.reply);
    } catch (err) {
      appendBubble('assistant', `Sorry, I couldn't answer that: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [draft, appendBubble]);

  // ─── Offering pick ───────────────────────────────────────────
  const pickOffering = useCallback(async (label: string) => {
    appendBubble('user', label);
    setBusy(true);
    try {
      const res = await onboardingChatApi.choice(label, chatState);
      setChatState(res.state);
      if (res.resolved && res.offering) {
        appendBubble('assistant', `Great — let's get you set up on ${res.offering.name}.`);
      } else {
        appendBubble('assistant', res.reply ?? "Sorry, I didn't catch that — Forge or Factory?");
      }
    } catch (err) {
      appendBubble('assistant', `Sorry, something went wrong: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [chatState, appendBubble]);

  const resetToQa = useCallback(() => {
    setChatState(initialOnboardingChatState());
  }, []);

  // ─── App idea composer (step: app_idea) ─────────────────────
  const submitAppIdea = useCallback(async () => {
    const idea = draft.trim();
    if (!idea) return;
    setDraft('');
    appendBubble('user', idea);
    setBusy(true);
    try {
      const res = await onboardingChatApi.appIdea(idea, chatState);
      setChatState(res.state);
      setSpecSessionId(res.specSessionId);
      appendBubble('assistant', "Got it — I've captured your idea. Starting your build now.");
    } catch (err) {
      appendBubble('assistant', `Sorry, I couldn't save that: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [draft, chatState, appendBubble]);

  // ─── Build trigger (client-only "building" phase) ───────────
  const triggerMaybeFire = useCallback(() => {
    if (chatState.offeringChoice !== 'factory' || !specSessionId) return;
    if (!isMaybeFireEnabled()) {
      setBuildInfo({ status: 'placeholder' });
      return;
    }
    setBuildInfo({ status: 'loading' });
    onboardingChatApi.maybeFire(specSessionId)
      .then((res) => {
        if (res.ready) {
          setBuildInfo({ status: 'enqueued', buildId: res.build_id, alreadyEnqueued: res.already_enqueued });
        } else if (res.missing?.includes('app_idea')) {
          setBuildInfo({ status: 'missing_app_idea' });
          appendBubble('assistant', "Looks like I lost track of your app idea — let's grab it again.");
          setSpecSessionId(null);
          setChatState(s => ({ ...s, step: 'app_idea' }));
        } else {
          setBuildInfo({ status: 'error', reason: res.reason ?? 'The build could not start yet.' });
        }
      })
      .catch((err) => setBuildInfo({ status: 'error', reason: err instanceof Error ? err.message : String(err) }));
  }, [chatState.offeringChoice, specSessionId, appendBubble]);

  useEffect(() => {
    if (screen !== 'building' || buildTriggered.current) return;
    buildTriggered.current = true;
    triggerMaybeFire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Reset the one-shot trigger guard if we bounce back to app_idea.
  useEffect(() => {
    if (screen === 'app_idea') buildTriggered.current = false;
  }, [screen]);

  const composerPlaceholder = screen === 'app_idea'
    ? 'Describe your app idea in plain English...'
    : 'Ask me anything about pricing, credits, or ownership...';

  const handleComposerSend = () => {
    if (screen === 'app_idea') void submitAppIdea();
    else void sendQa();
  };

  return (
    <div style={rootStyle}>
      <header style={headerStyle}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Let's get you set up</h1>
        <p style={{ ...dimStyle, margin: '4px 0 0' }}>
          {screen === 'payment' ? 'Checkout' :
            screen === 'app_idea' ? 'Tell me about your app' :
              screen === 'building' ? 'Building your app' :
                screen === 'survey' ? 'Almost there' : 'Welcome'}
        </p>
      </header>

      <div style={threadStyle}>
        {bubbles.map(b => (
          <div key={b.id} style={b.role === 'user' ? userBubbleStyle : assistantBubbleStyle}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{b.text}</p>
          </div>
        ))}

        {screen === 'qa' && !chatState.offeringChoice && (
          <OfferingPicker disabled={busy} onPick={pickOffering} />
        )}

        {screen === 'payment' && (
          <PaymentScreen
            offering={chatState.offeringChoice ?? 'factory'}
            onSucceeded={async () => {
              setBusy(true);
              try {
                const res = await onboardingChatApi.trialExplain(chatState);
                setChatState(res.state);
                appendBubble('assistant', res.message);
              } catch (err) {
                appendBubble('assistant', `Payment succeeded but I couldn't advance you: ${err instanceof Error ? err.message : String(err)}`);
              } finally {
                setBusy(false);
              }
            }}
            onSwitchToFactory={resetToQa}
          />
        )}

        {screen === 'building' && (
          <BuildingCard
            info={buildInfo}
            onRetry={triggerMaybeFire}
            onPersonalize={() => setPostBuildPhase('survey')}
            onFinish={onFinish}
          />
        )}

        {screen === 'survey' && (
          <SurveyCard
            state={chatState}
            onFinish={onFinish}
          />
        )}

        <div ref={threadEndRef} />
      </div>

      {(screen === 'qa' || screen === 'app_idea') && (
        <section style={composerStyle}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleComposerSend();
              }
            }}
            placeholder={composerPlaceholder}
            style={textareaStyle}
            disabled={busy}
            rows={2}
          />
          <button
            onClick={handleComposerSend}
            disabled={busy || !draft.trim()}
            style={primaryButtonStyle}
            type="button"
          >
            {busy ? 'Working...' : 'Send'}
          </button>
        </section>
      )}
    </div>
  );
}

// ─── Offering picker (Forge / Factory) ───────────────────────
function OfferingPicker({ disabled, onPick }: {
  disabled: boolean;
  onPick: (label: string) => void;
}): React.ReactElement {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, width: '100%' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onPick('Forge')}
        style={offeringCardStyle}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Starter</div>
        <div style={{ fontSize: 13, color: 'var(--text-dim, #9ca3af)', marginTop: 6 }}>
          Maintenance for the repo you already have.
          <p>Starter $50/month with 5,000 credits every month (included credits reset monthly). MCP access included.</p>
          <p>Top-up credits never expire.</p>
          <p>Each MCP run reserves 1,000 credits. Failed, cancelled or watchdog-killed MCP runs are fully refunded (net 0 credits); a successful run is capped at 1,000 credits; usage draws included credits first, then purchased top-ups.</p>
        </div>
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onPick('Factory')}
        style={offeringCardStyle}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Pro</div>
        <div style={{ fontSize: 13, color: 'var(--text-dim, #9ca3af)', marginTop: 6 }}>
          Builds a brand-new SaaS from your idea.
        </div>
      </button>
    </div>
  );
}

// ─── Payment / checkout screen ───────────────────────────────
export function PaymentScreen({ offering, onSucceeded, onSwitchToFactory }: {
  offering: OfferingId;
  onSucceeded: () => void;
  onSwitchToFactory: () => void;
}): React.ReactElement {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [discount, setDiscount] = useState<StripeDiscount | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState<{ url: string; plan: string } | null>(null);
  const prepared = useRef(false);

  useEffect(() => {
    if (prepared.current) return;
    prepared.current = true;
    onboardingChatApi.paymentPrepare(offering)
      .then((res) => {
        setLoading(false);
        if (res.error === 'checkout_already_pending') {
          const pending = res.pending_checkout;
          if (pending && ['forge', 'factory'].includes(pending.plan) && pending.billing_period === 'monthly') {
            try {
              const url = new URL(pending.url);
              if (url.protocol === 'https:' && url.hostname === 'invoice.stripe.com' && !url.port && !url.username && !url.password) {
                setPendingCheckout({ url: url.href, plan: pending.plan === 'forge' ? 'Forge' : 'Factory' });
                setError('An unpaid checkout already exists with a different plan or discount. Review its amount and discount on Stripe before paying.');
                return;
              }
            } catch { /* An unverified link must not become payable recovery. */ }
          }
          setError('A previous checkout is still pending. Contact support to review it.');
        } else if (res.stripe?.ready && res.stripe.client_secret) {
          setClientSecret(res.stripe.client_secret);
          setDiscount(res.stripe.discount ?? null);
        } else {
          setError(res.stripe?.note ?? res.error ?? 'Checkout is not available yet for this offering.');
        }
      })
      .catch((err) => {
        setLoading(false);
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [offering]);

  if (loading) {
    return <div style={cardStyle}><p style={dimStyle}>Preparing checkout...</p></div>;
  }

  if (!clientSecret) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: '0 0 12px' }}>{error ?? 'Checkout is not available yet.'}</p>
        {pendingCheckout && (
          <a href={pendingCheckout.url} style={primaryButtonStyle}>
            Review existing {pendingCheckout.plan} monthly checkout
          </a>
        )}
        {!pendingCheckout && offering === 'forge' && (
          <button type="button" onClick={onSwitchToFactory} style={primaryButtonStyle}>
            Switch to Factory
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {discount && (
        <p style={{ ...dimStyle, marginBottom: 12 }}>
          Discount applied{discount.name ? `: ${discount.name}` : ''}
        </p>
      )}
      <Elements stripe={getStripePromise()} options={{ clientSecret }}>
        <OnboardingCheckoutForm onSucceeded={onSucceeded} />
      </Elements>
    </div>
  );
}

export function OnboardingCheckoutForm({ onSucceeded }: { onSucceeded: () => void }): React.ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setPayError(null);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin },
      redirect: 'if_required',
    });
    if (error) {
      setPayError(error.message || 'Payment failed');
      setProcessing(false);
    } else {
      onSucceeded();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 16 }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      {payError && <p style={{ color: 'var(--red, #f87171)', fontSize: 13 }}>{payError}</p>}
      <button type="submit" disabled={!stripe || processing} style={primaryButtonStyle}>
        {processing ? 'Processing...' : 'Subscribe'}
      </button>
    </form>
  );
}

// ─── Building card ────────────────────────────────────────────
function BuildingCard({ info, onRetry, onPersonalize, onFinish }: {
  info: BuildInfo;
  onRetry: () => void;
  onPersonalize: () => void;
  onFinish: () => void;
}): React.ReactElement {
  if (info.status === 'loading' || info.status === 'idle') {
    return <div style={cardStyle}><p style={dimStyle}>Kicking off your build...</p></div>;
  }
  if (info.status === 'placeholder') {
    return (
      <div style={cardStyle}>
        <p style={{ margin: '0 0 12px' }}>You're all set — we'll email you when your app is ready.</p>
        <button type="button" onClick={onFinish} style={primaryButtonStyle}>Go to dashboard</button>
      </div>
    );
  }
  if (info.status === 'enqueued') {
    return (
      <div style={cardStyle}>
        <p style={{ margin: '0 0 8px' }}>
          {info.alreadyEnqueued ? 'Your build is already underway.' : 'Your build is underway!'}
        </p>
        {info.buildId && <p style={{ ...dimStyle, margin: '0 0 12px' }}>Build ID: {info.buildId}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onPersonalize} style={ghostButtonStyle}>Personalize your app</button>
          <button type="button" onClick={onFinish} style={primaryButtonStyle}>Go to dashboard</button>
        </div>
      </div>
    );
  }
  if (info.status === 'missing_app_idea') {
    return <div style={cardStyle}><p style={dimStyle}>One moment — let's grab your app idea again.</p></div>;
  }
  return (
    <div style={cardStyle}>
      <p style={{ margin: '0 0 12px' }}>{info.reason ?? 'Something went wrong starting your build.'}</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onRetry} style={primaryButtonStyle}>Try again</button>
        <button type="button" onClick={onFinish} style={ghostButtonStyle}>Skip for now</button>
      </div>
    </div>
  );
}

// ─── Optional post-build survey (credential entry) ───────────
function SurveyCard({ state, onFinish }: {
  state: OnboardingChatState;
  onFinish: () => void;
}): React.ReactElement {
  const [requirements, setRequirements] = useState<Array<{ service?: string; keyName?: string; label?: string }>>([]);
  const [githubInstallUrl, setGithubInstallUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    onboardingChatApi.survey(state)
      .then((res) => {
        setRequirements((res.requirements ?? []) as Array<{ service?: string; keyName?: string; label?: string }>);
        setGithubInstallUrl(res.githubInstallUrl);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (req: { service?: string; keyName?: string }) => {
    if (!req.service || !req.keyName) return;
    const key = `${req.service}:${req.keyName}`;
    const value = values[key];
    if (!value) return;
    try {
      await onboardingChatApi.surveySubmit({ service: req.service, keyName: req.keyName, value });
      setSaved(prev => ({ ...prev, [key]: true }));
    } catch {
      // best-effort — never blocks
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ margin: '0 0 12px' }}>
        Your app is building — want to personalize it now, or do this later? Nothing here blocks your build.
      </p>
      {loading && <p style={dimStyle}>Loading...</p>}
      {!loading && requirements.length === 0 && (
        <p style={dimStyle}>Nothing needed right now.</p>
      )}
      {!loading && requirements.map((req, i) => {
        const key = `${req.service ?? ''}:${req.keyName ?? ''}`;
        return (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input
              type="password"
              placeholder={req.label ?? req.keyName ?? req.service ?? 'value'}
              value={values[key] ?? ''}
              onChange={(e) => setValues(v => ({ ...v, [key]: e.target.value }))}
              style={textInputStyle}
            />
            <button type="button" onClick={() => save(req)} style={ghostButtonStyle} disabled={saved[key]}>
              {saved[key] ? 'Saved' : 'Save'}
            </button>
          </div>
        );
      })}
      {githubInstallUrl && (
        <p style={dimStyle}><a href={githubInstallUrl} target="_blank" rel="noreferrer">Install the GitHub App</a></p>
      )}
      <button type="button" onClick={onFinish} style={{ ...primaryButtonStyle, marginTop: 12 }}>
        Go to dashboard
      </button>
    </div>
  );
}

// ─── Styles (reuses SubscriberChat's visual language) ────────

const rootStyle: React.CSSProperties = {
  maxWidth: 880,
  margin: '0 auto',
  padding: '20px 16px 24px',
  fontFamily: 'var(--font-sans, system-ui)',
  color: 'var(--text, #e5e7eb)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minHeight: 'calc(100vh - 48px)',
};

const headerStyle: React.CSSProperties = {
  paddingBottom: 12,
  borderBottom: '1px solid var(--border, #2a2f3a)',
};

const dimStyle: React.CSSProperties = {
  color: 'var(--text-dim, #9ca3af)',
  fontSize: 13,
};

const threadStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  flex: 1,
  paddingBottom: 8,
};

const userBubbleStyle: React.CSSProperties = {
  alignSelf: 'flex-end',
  maxWidth: '82%',
  padding: '10px 14px',
  borderRadius: '14px 14px 4px 14px',
  background: 'var(--blue, #3b82f6)',
  color: '#fff',
  fontSize: 14,
  lineHeight: 1.4,
};

const assistantBubbleStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  maxWidth: '82%',
  padding: '10px 14px',
  borderRadius: '14px 14px 14px 4px',
  background: 'var(--panel-alt, #0f172a)',
  border: '1px solid var(--border, #2a2f3a)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 14,
  lineHeight: 1.4,
};

const cardStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  width: '100%',
  padding: 18,
  borderRadius: 10,
  border: '1px solid var(--blue, #3b82f6)',
  background: 'var(--panel, #111827)',
};

const offeringCardStyle: React.CSSProperties = {
  cursor: 'pointer',
  textAlign: 'left',
  padding: 16,
  border: '1px solid var(--border, #2a2f3a)',
  borderRadius: 10,
  background: 'var(--panel, #111827)',
  color: 'var(--text, #e5e7eb)',
};

const composerStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-end',
  padding: 10,
  border: '1px solid var(--border, #2a2f3a)',
  borderRadius: 8,
  background: 'var(--panel, #111827)',
  position: 'sticky',
  bottom: 10,
};

const textareaStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 44,
  padding: 10,
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'var(--bg, #0b1020)',
  color: 'var(--text, #e5e7eb)',
  fontFamily: 'inherit',
  fontSize: 14,
  resize: 'vertical',
};

const textInputStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 40,
  padding: '0 12px',
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'var(--bg, #0b1020)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 14,
};

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 18px',
  border: 'none',
  borderRadius: 6,
  background: 'var(--blue, #3b82f6)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};

const ghostButtonStyle: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 14px',
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text, #e5e7eb)',
  cursor: 'pointer',
  fontSize: 13,
};
